import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Users, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TODAY = () => new Date(new Date().setHours(0, 0, 0, 0));

const parseDate = (v) => {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d) || d < TODAY() ? null : d;
};

const buildInitialForm = (params) => {
  const checkIn = parseDate(params.get("check_in"));
  const checkOut = parseDate(params.get("check_out"));
  const guests = Number(params.get("guests"));
  return {
    full_name: "",
    email: "",
    phone: "",
    room_slug: params.get("room") || "",
    check_in: checkIn,
    check_out: checkOut && checkIn && checkOut > checkIn ? checkOut : null,
    guests: [1, 2].includes(guests) ? guests : 2,
    special_requests: "",
  };
};

const buildPayload = (form) => ({
  full_name: form.full_name,
  email: form.email,
  phone: form.phone || null,
  room_slug: form.room_slug,
  check_in: format(form.check_in, "yyyy-MM-dd"),
  check_out: format(form.check_out, "yyyy-MM-dd"),
  guests: Number(form.guests),
  special_requests: form.special_requests || null,
});

const editorialInput =
  "bg-transparent border-0 border-b border-ink-900/15 rounded-none h-12 px-0 text-ink-900 placeholder:text-ink-400 focus-visible:ring-0 focus-visible:border-sage";

const DateField = ({ label, value, onSelect, disabled, testId }) => (
  <div>
    <Label className="overline">
      <CalendarIcon className="inline w-3 h-3 mr-2 -mt-1" />
      {label}
    </Label>
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`${editorialInput} w-full flex items-center justify-between text-left`}
        >
          {value ? format(value, "EEE, d LLL") : <span className="text-ink-400">Select</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-paper-50 border-ink-900/10 text-ink-900 p-0">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onSelect}
          disabled={disabled}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  </div>
);

const ContactFields = ({ form, update }) => (
  <div className="space-y-6">
    {[
      ["full_name", "Full Name", "Your name", "text", true],
      ["email", "Email", "you@example.com", "email", true],
      ["phone", "Phone (optional)", "+91 …", "tel", false],
    ].map(([key, label, placeholder, type, required]) => (
      <div key={key}>
        <Label className="overline">{label}</Label>
        <Input
          required={required}
          type={type}
          data-testid={`booking-input-${key === "full_name" ? "name" : key}`}
          value={form[key]}
          onChange={(e) => update(key, e.target.value)}
          placeholder={placeholder}
          className={editorialInput}
        />
      </div>
    ))}
  </div>
);

const RoomSelect = ({ rooms, value, onChange }) => (
  <div>
    <Label className="overline">
      <BedDouble className="inline w-3 h-3 mr-2 -mt-1" />
      Room
    </Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="booking-room-trigger" className={`${editorialInput} justify-between`}>
        <SelectValue placeholder="Select a room category" />
      </SelectTrigger>
      <SelectContent className="bg-paper-50 border-ink-900/10 text-ink-900">
        {rooms.map((r) => (
          <SelectItem
            key={r.slug}
            value={r.slug}
            data-testid={`booking-room-option-${r.slug}`}
            className="focus:bg-paper-100 focus:text-sage-dark"
          >
            {r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const GuestSelect = ({ value, options, onChange }) => (
  <div>
    <Label className="overline">
      <Users className="inline w-3 h-3 mr-2 -mt-1" />
      Guests
    </Label>
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger data-testid="booking-guests-trigger" className={`${editorialInput} justify-between`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-paper-50 border-ink-900/10 text-ink-900">
        {options.map((n) => (
          <SelectItem
            key={n}
            value={String(n)}
            data-testid={`booking-guests-${n}`}
            className="focus:bg-paper-100 focus:text-sage-dark"
          >
            {n} {n === 1 ? "guest" : "guests"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const SummaryBar = ({ selectedRoom, nights, loading }) => (
  <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t hairline">
    <div className="text-sm text-ink-600">
      {selectedRoom && nights > 0 ? (
        <>
          <span className="text-ink-900">{selectedRoom.name}</span> · {nights}{" "}
          {nights === 1 ? "night" : "nights"} ·{" "}
          <span className="text-sage-dark italic">Rates confirmed by reservations</span>
        </>
      ) : (
        <span className="text-ink-500">
          Tariffs vary by season. Our reservations team will confirm rates by email.
        </span>
      )}
    </div>
    <Button
      type="submit"
      disabled={loading}
      data-testid="booking-submit-btn"
      className="h-12 px-10 bg-sage hover:bg-sage-dark text-paper-50 rounded-none text-[12px] tracking-[0.22em] uppercase"
    >
      {loading ? "Submitting…" : "Request reservation"}
    </Button>
  </div>
);

export const BookingForm = ({ variant = "full" }) => {
  const [rooms, setRooms] = useState([]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => buildInitialForm(params));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/rooms`)
      .then((r) => setRooms(r.data))
      .catch(() => toast.error("Could not load rooms"));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const nights =
    form.check_in && form.check_out
      ? Math.max(0, Math.round((form.check_out - form.check_in) / (1000 * 60 * 60 * 24)))
      : 0;
  const selectedRoom = rooms.find((r) => r.slug === form.room_slug);
  const getMaxGuests = () => {
    if (selectedRoom) return selectedRoom.max_guests;
    if (rooms.length) return Math.max(...rooms.map((r) => r.max_guests));
    return 2;
  };
  const maxGuests = getMaxGuests();
  const guestOptions = Array.from({ length: maxGuests }, (_, i) => i + 1);

  useEffect(() => {
    setForm((f) => (f.guests > maxGuests ? { ...f, guests: maxGuests } : f));
  }, [maxGuests]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.room_slug) return toast.error("Please select a room");
    if (!form.check_in || !form.check_out) return toast.error("Please select dates");
    if (nights < 1) return toast.error("Check-out must be after check-in");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, buildPayload(form));
      trackEvent("generate_lead", {
        lead_type: "booking_enquiry",
        room_name: data.room_name,
        nights,
        guests: form.guests,
      });
      toast.success("Reservation request received", {
        description: `We'll confirm ${data.room_name} availability and rates shortly.`,
      });
      navigate(`/book/confirmation/${data.id}`, { state: { booking: data } });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit reservation";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className={`grid gap-8 ${variant === "full" ? "md:grid-cols-2" : "md:grid-cols-1"}`}
    >
      <ContactFields form={form} update={update} />

      <div className="space-y-6">
        <RoomSelect rooms={rooms} value={form.room_slug} onChange={(v) => update("room_slug", v)} />

        <div className="grid grid-cols-2 gap-4">
          <DateField
            label="Check in"
            value={form.check_in}
            onSelect={(d) => update("check_in", d)}
            disabled={(d) => d < TODAY()}
            testId="booking-checkin-trigger"
          />
          <DateField
            label="Check out"
            value={form.check_out}
            onSelect={(d) => update("check_out", d)}
            disabled={(d) => d < TODAY() || (form.check_in && d <= form.check_in)}
            testId="booking-checkout-trigger"
          />
        </div>

        <GuestSelect value={form.guests} options={guestOptions} onChange={(v) => update("guests", v)} />
      </div>

      <div className="md:col-span-2">
        <Label className="overline">Special requests</Label>
        <Textarea
          data-testid="booking-input-requests"
          value={form.special_requests}
          onChange={(e) => update("special_requests", e.target.value)}
          placeholder="Late arrival, anniversary, dietary notes…"
          className="bg-transparent border border-ink-900/15 rounded-none min-h-[110px] text-ink-900 placeholder:text-ink-400 focus-visible:ring-0 focus-visible:border-sage"
        />
      </div>

      <SummaryBar selectedRoom={selectedRoom} nights={nights} loading={loading} />
    </form>
  );
};

export default BookingForm;
